import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import ReactHeader from "./reactHeader";
import FooterSection from "./footerSection";
import AnimatedBlock from "./animatedBlock";
import "./itSecurity.css";

const CLIENT_ID = "7585e8a9-cc66-4c1c-8a3f-299ae3798660";
const CONSENT_LINK = `https://login.microsoftonline.com/organizations/adminconsent?client_id=${CLIENT_ID}&redirect_uri=https%3A%2F%2Fwww.khordeveloper.com%2F`;
const GOOGLE_CLIENT_ID = "420347927717-6r366hqc7dqop768i4a4fn74ih8gp8k5.apps.googleusercontent.com";
const THUMBPRINT = "DA2A0B9025928E8D24DA7469B553FBF2ACA19A9D";
const LAST_UPDATED = "November 3, 2026";

const BLADE_LABELS = {
    trust: "Data, Security & Trust",
    setup: "Setup for IT",
};

const TRUST_NAV = [
    { href: "#summary", label: "Summary" },
    { href: "#key-facts", label: "Key Facts" },
    { href: "#data", label: "Your Data" },
    { href: "#faq", label: "Common Questions" },
];

const SETUP_NAV_GROUPS = [
    {
        title: "Steps",
        items: [
            { href: "#step-consent", label: "1. Allow Microsoft or Google Sign-In" },
            { href: "#step-signing", label: "2. Allow the Installer and Certificate" },
            { href: "#step-endpoints", label: "3. Allow Network Endpoints" },
            { href: "#step-ai", label: "4. Decide on Khor AI" },
        ],
    },
    {
        title: "Reference",
        items: [
            { href: "#install", label: "Installation and Updates" },
            { href: "#signing", label: "Code Signing and Allow-Listing" },
            { href: "#endpoints", label: "Network Endpoints" },
            { href: "#permissions", label: "Permissions and Technical Details" },
            { href: "#contact", label: "Contact" },
        ],
    },
];

const ANCHOR_BLADE = {
    ...Object.fromEntries(TRUST_NAV.map((item) => [item.href.slice(1), "trust"])),
    ...Object.fromEntries(
        SETUP_NAV_GROUPS.flatMap((group) => group.items).map((item) => [item.href.slice(1), "setup"])
    ),
};

const GLANCE_ITEMS = [
    {
        label: "Where it runs",
        body: "The add-in is a VSTO (.NET/C#) component that loads in-process with Excel and executes entirely on the user's device. The only exception is the optional Khor AI feature (see Your Data).",
    },
    {
        label: "Sign-in",
        body: "Delegated to Microsoft or Google. Users sign in with their Microsoft work account or Google Workspace account through Azure AD B2C; we never see or store passwords. Sign-in records are kept by your identity provider and in our Azure AD B2C directory.",
    },
    {
        label: "Permissions",
        body: "Like any desktop Excel add-in, Khor runs with the user's normal Windows permissions. It doesn't request elevation, runs only while Excel is open, writes only to the user's own profile, and connects only to the endpoints listed under Setup for IT.",
    },
    {
        label: "Code integrity",
        body: "Code-signed with an organization-validated GlobalSign Authenticode certificate, recognized natively by Windows. Signatures are timestamped. This confirms the software is unmodified and comes from a verified publisher.",
    },
    {
        label: "Data we store",
        body: "A name and work email for sign-in and licensing. For Khor AI users only, a monthly usage record (no document contents).",
    },
    {
        label: "Business insurance",
        body: "Technology & Media Errors and Omissions ($1,000,000 aggregate), including cyber liability coverage, and Commercial General Liability ($1,000,000 per occurrence / $2,000,000 aggregate). Certificate of insurance available on request.",
    },
];

const ENDPOINTS = [
    { endpoint: "khordeveloperinstaller.blob.core.windows.net", usedFor: "Installer and updates", when: "Install; every Excel start" },
    { endpoint: "khordeveloperb2c.b2clogin.com", usedFor: "Sign-in (Azure AD B2C)", when: "Sign-in and token refresh" },
    { endpoint: "login.microsoftonline.com", usedFor: "Microsoft work-account sign-in (Microsoft users only)", when: "Sign-in" },
    { endpoint: "accounts.google.com", usedFor: "Google Workspace sign-in (Google users only)", when: "Sign-in" },
    { endpoint: "localhost:52471 (HTTP, loopback only, never leaves the PC)", usedFor: "Receiving the sign-in result", when: "Sign-in" },
    { endpoint: "khor-agent-relay.azurewebsites.net", usedFor: "Khor AI (BETA)", when: "Only when a user it's enabled for runs Khor AI" },
    { endpoint: "www.khordeveloper.com", usedFor: "Help and access pages in the browser", when: "When the user clicks a link" },
    { endpoint: "go.microsoft.com, download.microsoft.com", usedFor: ".NET 4.8 and VSTO runtime", when: "Install, only if missing" },
    { endpoint: "crl.globalsign.com, ocsp.globalsign.com, secure.globalsign.com", usedFor: "Certificate revocation and chain checks by Windows", when: "Install and update" },
];

const CopyButton = ({ value, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard API unavailable or blocked — nothing more we can do.
        }
    };

    return (
        <button
            type="button"
            className="its-copy-btn"
            onClick={handleCopy}
            aria-label={`Copy ${label}`}
        >
            {copied ? "Copied" : "Copy"}
        </button>
    );
};

const MonoBox = ({ value, label }) => (
    <div className="its-mono-row">
        <code className="its-mono">{value}</code>
        <CopyButton value={value} label={label} />
    </div>
);

const DataListRow = ({ label, children }) => (
    <div className="its-data-list-row">
        <div className="its-data-list-label">{label}</div>
        <div className="its-data-list-value">{children}</div>
    </div>
);

const StepMeta = ({ who, skipIf, defaultText }) => (
    <p className="its-step-meta">
        <strong>Who:</strong> {who}
        {skipIf && <>{" "}&nbsp;·&nbsp; <strong>Skip if:</strong> {skipIf}</>}
        {defaultText && <>{" "}&nbsp;·&nbsp; <strong>Default:</strong> {defaultText}</>}
    </p>
);

function detectInitialState() {
    const hash = typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
    if (hash === "trust" || hash === "setup") {
        return { blade: hash, scrollTo: null };
    }
    const blade = ANCHOR_BLADE[hash];
    return blade ? { blade, scrollTo: hash } : { blade: "trust", scrollTo: null };
}

const ItSecuritySection = () => {
    const initial = useState(detectInitialState)[0];
    const [activeBlade, setActiveBlade] = useState(initial.blade);
    const [pendingScrollId, setPendingScrollId] = useState(initial.scrollTo);

    useEffect(() => {
        if (!pendingScrollId) return;
        const el = document.getElementById(pendingScrollId);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        setPendingScrollId(null);
        // Wait for the blade's content to mount before trying to scroll to it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBlade, pendingScrollId]);

    const switchBlade = (key) => {
        if (typeof window !== "undefined") window.history.replaceState(null, "", `#${key}`);
        setActiveBlade(key);
    };

    const jumpTo = (id) => (e) => {
        e.preventDefault();
        const targetBlade = ANCHOR_BLADE[id];
        if (!targetBlade) return;
        if (typeof window !== "undefined") window.history.replaceState(null, "", `#${id}`);
        if (targetBlade !== activeBlade) {
            setActiveBlade(targetBlade);
            setPendingScrollId(id);
        } else {
            document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <section className="its-section">
            <div className="its-container">
                <AnimatedBlock>
                    <p className="its-eyebrow">For IT &amp; Security Teams</p>
                    <h1 className="its-headline">IT &amp; Security</h1>
                    <p className="its-subtext">
                        Information for IT and security teams approving Khor Developer, our Excel add-in for
                        commercial real estate underwriting.
                    </p>
                    <p className="its-updated">Last updated: {LAST_UPDATED}</p>
                </AnimatedBlock>

                <AnimatedBlock>
                    <div className="its-tabs" role="tablist" aria-label="IT & Security sections">
                        {Object.entries(BLADE_LABELS).map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={activeBlade === key}
                                className={`its-tab${activeBlade === key ? " its-tab--active" : ""}`}
                                onClick={() => switchBlade(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {activeBlade === "trust" ? (
                        <nav className="its-local-toc" aria-label="Data, Security & Trust sections">
                            {TRUST_NAV.map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    className="its-local-toc-link"
                                    onClick={jumpTo(item.href.slice(1))}
                                >
                                    {item.label}
                                </a>
                            ))}
                        </nav>
                    ) : (
                        <nav className="its-local-toc its-local-toc--grouped" aria-label="Setup for IT sections">
                            {SETUP_NAV_GROUPS.map((group) => (
                                <div className="its-local-toc-group" key={group.title}>
                                    <p className="its-local-toc-group-title">{group.title}</p>
                                    {group.items.map((item) => (
                                        <a
                                            key={item.href}
                                            href={item.href}
                                            className="its-local-toc-link"
                                            onClick={jumpTo(item.href.slice(1))}
                                        >
                                            {item.label}
                                        </a>
                                    ))}
                                </div>
                            ))}
                        </nav>
                    )}
                </AnimatedBlock>

                {activeBlade === "trust" && (
                    <>
                        <AnimatedBlock>
                            <section className="its-block" id="summary">
                                <h2>Summary</h2>
                                <p>
                                    Khor Developer runs inside Excel on your users' PCs. Your deal, model and
                                    property data stays there, except for documents a user chooses to send to the
                                    optional Khor AI feature (see <a href="#data" onClick={jumpTo("data")}>Your Data</a>). The only customer
                                    information we hold is a name and work email address for sign-in and
                                    licensing, plus monthly usage records for users of Khor AI.
                                </p>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="key-facts">
                                <h2>Key Facts</h2>
                                <div className="its-keyfacts-table">
                                    {GLANCE_ITEMS.map((item) => (
                                        <div className="its-keyfacts-row" key={item.label}>
                                            <div className="its-keyfacts-label">{item.label}</div>
                                            <div className="its-keyfacts-body">{item.body}</div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="data">
                                <h2>Your Data</h2>

                                <div className="its-data-card its-data-card--accent">
                                    <span className="its-data-badge its-data-badge--on-device">Stays on Your Device</span>
                                    <h3>Your Models/Workbooks</h3>
                                    <ul>
                                        <li>Models, calculations and Argus/.khor imports are processed entirely on the user's PC.</li>
                                        <li>Workbooks stay wherever the user saves them: locally, on OneDrive or on SharePoint. We never upload them.</li>
                                        <li>Before an older workbook is upgraded to a new Khor version, a backup is saved on the PC (<code className="its-inline-mono">%APPDATA%\Khor\Backups\</code>) and deleted automatically after 90 days.</li>
                                    </ul>
                                </div>

                                <div className="its-data-card">
                                    <span className="its-data-badge its-data-badge--exception">Optional — Leaves Your Device</span>
                                    <h3>Khor AI (BETA)</h3>
                                    <p>
                                        Khor AI reads documents a user selects, such as rent rolls, operating
                                        statements or offering memorandums, and pulls the key figures into their
                                        Khor model. It's off by default. Khor enables it only for specific users,
                                        at their organization's request, and even then it runs only when that user
                                        chooses to use it.
                                    </p>
                                    <div className="its-data-list">
                                        <DataListRow label="What's sent">
                                            The full contents of the documents the user selects (PDFs, images, CSV/TXT, and
                                            Excel, Word or PowerPoint files converted on the PC first).
                                        </DataListRow>
                                        <DataListRow label="Where it goes">
                                            Our relay service on Microsoft Azure (East US 2), which passes it to Anthropic's
                                            Claude API for processing.
                                        </DataListRow>
                                        <DataListRow label="What's kept">
                                            Our relay doesn't store or log document contents. Anthropic deletes API inputs
                                            and outputs within 30 days by default, except where needed to enforce its Usage
                                            Policy or by law, and doesn't use commercial API data for training by default.
                                        </DataListRow>
                                        <DataListRow label="Your control">
                                            Khor AI stays off unless your organization asks us to enable it for specific
                                            users. For everyone else, our server refuses AI requests and the feature is
                                            locked in the add-in.
                                        </DataListRow>
                                    </div>
                                </div>

                                <div className="its-data-card">
                                    <span className="its-data-badge its-data-badge--exception">What We Hold</span>
                                    <h3>Data We Store</h3>
                                    <div className="its-data-list">
                                        <DataListRow label="Account">
                                            Name and work email, in our Azure AD B2C directory. Used for sign-in and
                                            licensing.
                                        </DataListRow>
                                        <DataListRow label="Khor AI usage">
                                            For users it's enabled for only: a monthly record per user (user ID, email,
                                            usage amounts, first and last use) plus basic request and error logs. Stored
                                            in Microsoft Azure. Never document contents.
                                        </DataListRow>
                                        <DataListRow label="Retention">
                                            Account and usage records are kept for the life of your subscription and
                                            deleted within 30 days of cancellation or on request. Request and error
                                            logs are deleted automatically after 90 days.
                                        </DataListRow>
                                    </div>
                                </div>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="faq">
                                <h2>Common Questions</h2>
                                <div className="its-faq-list">
                                    <div className="its-faq-item">
                                        <h3 className="its-faq-q">Where is our data hosted?</h3>
                                        <p className="its-faq-a">
                                            Workbooks and models aren't hosted by us; they stay on your PCs or wherever you save
                                            them (OneDrive, SharePoint). The only exception is documents a user sends to Khor AI
                                            once it's enabled for them, which our relay doesn't store; see{" "}
                                            <a href="#data" onClick={jumpTo("data")}>Your Data</a>.
                                        </p>
                                    </div>
                                    <div className="its-faq-item">
                                        <h3 className="its-faq-q">How is access controlled?</h3>
                                        <p className="its-faq-a">Through your own identity provider. For Microsoft 365 organizations, Entra ID MFA and conditional access apply, and after consent Khor Developer appears in Enterprise applications, where you can limit it to certain users or turn it off. For Google Workspace organizations, your Google sign-in policies apply, and admins can control access under API controls.</p>
                                    </div>
                                    <div className="its-faq-item">
                                        <h3 className="its-faq-q">What if we stop using Khor, or Khor goes away?</h3>
                                        <p className="its-faq-a">We hold no deal data or files, so there's nothing to retrieve or lose. Your workbooks and their values stay with you. Documents sent to Khor AI are deleted by Anthropic within 30 days by default.</p>
                                    </div>
                                    <div className="its-faq-item">
                                        <h3 className="its-faq-q">How do we know the software hasn't been tampered with?</h3>
                                        <p className="its-faq-a">Every installer and update is code-signed by Khor Developer LLC (GlobalSign, organization-validated) and timestamped; Windows verifies the signature on install and update.</p>
                                    </div>
                                    <div className="its-faq-item">
                                        <h3 className="its-faq-q">Is Khor a real, stable vendor?</h3>
                                        <p className="its-faq-a">Registered New York LLC with E&amp;O/cyber and general liability insurance; W-9, certificate of insurance and NDA/DPA available on request.</p>
                                    </div>
                                </div>
                            </section>
                        </AnimatedBlock>
                    </>
                )}

                {activeBlade === "setup" && (
                    <>
                        <AnimatedBlock>
                            <section className="its-block" id="step-consent">
                                <h2>Step 1 — Allow Microsoft or Google Sign-In</h2>
                                <StepMeta
                                    who="Microsoft: Global, Cloud Application or Application Administrator · Google: Google Workspace super admin"
                                    skipIf="your users can already sign in"
                                />
                                <p>
                                    Users can sign in with a Microsoft work account or a Google Workspace account.
                                    Follow the section for your organization.
                                </p>

                                <h3>Microsoft 365 / Entra ID Organizations</h3>
                                <p>
                                    If your tenant requires admin consent for third-party apps, users see an "Approval
                                    required" screen when they sign in. A Global Administrator, Cloud Application
                                    Administrator or Application Administrator can grant consent for your organization here:
                                </p>
                                <MonoBox value={CONSENT_LINK} label="admin consent link" />
                                <p>After you accept, you're redirected to our homepage. That means consent was applied.</p>

                                <ul>
                                    <li>App name: Khor Developer</li>
                                    <li>
                                        Application (client) ID: <span className="its-inline-mono-wrap"><code className="its-inline-mono">{CLIENT_ID}</code><CopyButton value={CLIENT_ID} label="client ID" /></span>
                                    </li>
                                    <li>
                                        Permissions: <code className="its-inline-mono">User.Read</code> (Microsoft Graph, shown as "Sign in and read user profile") and <code className="its-inline-mono">offline_access</code> (shown as "Maintain access to data you have given it access to", which keeps the user signed in without repeated prompts). Used only to identify the user and show their name and email. We don't access mail, files, calendars or any other Microsoft 365 data.
                                    </li>
                                    <li>
                                        Sign-in runs through Azure AD B2C (tenant <code className="its-inline-mono">khordeveloperb2c</code>), federated to your users' Microsoft work accounts, so your existing MFA and conditional access policies apply. We never see or store passwords.
                                    </li>
                                </ul>

                                <details className="its-details">
                                    <summary>Technical details</summary>
                                    <ul>
                                        <li>
                                            Sign-in opens the user's default browser, and the result is returned to the add-in on a local loopback address (<code className="its-inline-mono">http://localhost:52471</code>) that never leaves the PC.
                                        </li>
                                        <li>
                                            Troubleshooting: on locked-down machines that block local listeners, an administrator can run <code className="its-inline-mono">netsh http add iplisten 127.0.0.1</code>.
                                        </li>
                                        <li>
                                            The sign-in token is cached at <code className="its-inline-mono">%LOCALAPPDATA%\KhorDeveloper\TokenCache.bin</code>, encrypted with Windows DPAPI so only that Windows user can read it.
                                        </li>
                                    </ul>
                                </details>

                                <h3>Google Workspace Organizations</h3>
                                <p>
                                    Users sign in with their Google Workspace account; Microsoft admin consent
                                    doesn't apply. Khor Developer requests only basic sign-in information (name,
                                    email and profile), so most organizations need no action.
                                </p>
                                <p>
                                    If your organization blocks unconfigured third-party apps, a Google Workspace
                                    admin can allow Khor Developer:
                                </p>
                                <ol className="its-nested-list">
                                    <li><strong>Admin console</strong> → <strong>Security</strong> → <strong>Access and data control</strong> → <strong>API controls</strong></li>
                                    <li><strong>Manage Third-Party App Access</strong> → <strong>Configure new app</strong></li>
                                    <li>Paste the client ID below into the search box and set access to <strong>Trusted</strong></li>
                                </ol>
                                <ul>
                                    <li>App name: Khor Developer</li>
                                    <li>
                                        Google OAuth client ID: <span className="its-inline-mono-wrap"><code className="its-inline-mono">{GOOGLE_CLIENT_ID}</code><CopyButton value={GOOGLE_CLIENT_ID} label="Google OAuth client ID" /></span>
                                    </li>
                                    <li>Your Google Workspace sign-in policies, such as 2-Step Verification, apply. We never see or store passwords.</li>
                                </ul>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="step-signing">
                                <h2>Step 2 — Allow the Installer and Certificate</h2>
                                <StepMeta
                                    who="Endpoint security / Defender admin"
                                    skipIf="users can download and run the installer without a warning"
                                />
                                <p>Two checks can affect the installer:</p>
                                <ul>
                                    <li>
                                        <strong>When downloading:</strong> Microsoft Edge uses Defender SmartScreen; Chrome and
                                        Firefox use Google Safe Browsing, which may show "This file isn't commonly downloaded."
                                        Because our certificate is relatively new, either may warn until its reputation builds.
                                        Web filters or proxies may also block downloads from
                                        khordeveloperinstaller.blob.core.windows.net.
                                    </li>
                                    <li>
                                        <strong>When running:</strong> Windows checks the installer whichever browser
                                        downloaded it (Windows SmartScreen, Smart App Control, AppLocker, WDAC, or Defender for
                                        Endpoint).
                                    </li>
                                </ul>
                                <p>Options:</p>
                                <ul>
                                    <li><strong>Recommended:</strong> allow by publisher (<code className="its-inline-mono">KHOR DEVELOPER LLC</code> plus issuing CA GlobalSign GCC R45 CodeSigning CA 2020) in AppLocker or WDAC. These rules carry over when our certificate renews.</li>
                                    <li><strong>Alternative:</strong> add <code className="its-inline-mono">KhorCodeSigning.cer</code> as a Defender certificate indicator, or deploy it to Trusted Publishers via Group Policy or Intune. Trusted Publishers also removes the first-install prompt. Either option needs the new certificate added at each annual renewal.</li>
                                    <li>Allow the download location — see <a href="#install" onClick={jumpTo("install")}>Installation and Updates</a> below.</li>
                                </ul>
                                <p>SmartScreen may briefly warn again after a certificate renewal while the new certificate builds reputation.</p>
                                <div className="its-data-list">
                                    <DataListRow label="Current certificate thumbprint (SHA-1), valid to 9 February 2027">
                                        <MonoBox value={THUMBPRINT} label="thumbprint" />
                                    </DataListRow>
                                    <DataListRow label="Download">
                                        <a href="/KhorCodeSigning.cer" download className="its-download-link">KhorCodeSigning.cer</a> (public certificate only)
                                    </DataListRow>
                                </div>
                                <p>
                                    See full <a href="#signing" onClick={jumpTo("signing")}>Code Signing and Allow-Listing</a> below
                                    for the publisher, issuer and serial number.
                                </p>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="step-endpoints">
                                <h2>Step 3 — Allow Network Endpoints</h2>
                                <StepMeta
                                    who="Network / firewall team"
                                    skipIf="you don't filter outbound traffic"
                                />
                                <p>
                                    Khor Developer connects to a small set of Microsoft, Google, GlobalSign and Khor-operated
                                    domains, all over HTTPS. See the full list and when each one is used in{" "}
                                    <a href="#endpoints" onClick={jumpTo("endpoints")}>Network Endpoints</a> below.
                                </p>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="step-ai">
                                <h2>Step 4 — Decide on Khor AI (BETA)</h2>
                                <StepMeta who="Whoever approves the vendor" defaultText="off" />
                                <p>
                                    Khor AI is off by default and only runs for specific users at your organization's
                                    request — see <a href="#data" onClick={jumpTo("data")}>Your Data</a> for exactly what
                                    it can access and where that data goes.
                                </p>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <div className="its-reference-divider">
                                <h2 className="its-reference-heading">Reference</h2>
                                <p className="its-reference-subtext">
                                    Full technical detail for each step above.
                                </p>
                            </div>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="install">
                                <h2>Installation and Updates</h2>
                                <ul>
                                    <li>
                                        Installer: <code className="its-inline-mono">https://khordeveloperinstaller.blob.core.windows.net/khordeveloperaddin/KhorDeveloperSetup.exe</code> (standard Microsoft ClickOnce installer).
                                    </li>
                                    <li>First install: unless our certificate is in the machine's Trusted Publishers store, the user sees a one-time Office prompt, "Do you want to install this customization?"</li>
                                    <li>
                                        Updates: each time Excel starts, the add-in checks <code className="its-inline-mono">https://khordeveloperinstaller.blob.core.windows.net/khordeveloperaddin/Khor Developer.vsto</code> over HTTPS and installs any new version. No admin rights are needed. After our annual certificate renewal, users may see a one-time Office prompt to trust the update, unless our new certificate is in Trusted Publishers.
                                    </li>
                                    <li>Uninstall: Settings → Apps → Installed apps → Khor Developer.</li>
                                    <li>We never send the installer as an email attachment. Only download it from the link above.</li>
                                </ul>
                                <details className="its-details">
                                    <summary>Technical details: what the installer does</summary>
                                    <ol className="its-nested-list">
                                        <li>Installs .NET 4.8 and the VSTO runtime from Microsoft if missing.</li>
                                        <li>Installs the add-in per user (<code className="its-inline-mono">%LOCALAPPDATA%\Apps\2.0\</code>).</li>
                                        <li>Registers it under <code className="its-inline-mono">HKCU\Software\Microsoft\Office\Excel\Addins\Khor Developer</code>.</li>
                                    </ol>
                                </details>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="signing">
                                <h2>Code Signing and Allow-Listing</h2>

                                <div className="its-table-wrap">
                                    <table className="its-table">
                                        <tbody>
                                            <tr><td>Publisher (subject)</td><td><code className="its-inline-mono">CN=KHOR DEVELOPER LLC, O=KHOR DEVELOPER LLC, L=Chappaqua, S=New York, C=US</code></td></tr>
                                            <tr><td>Issuer</td><td>GlobalSign GCC R45 CodeSigning CA 2020 (root: GlobalSign Code Signing Root R45)</td></tr>
                                            <tr><td>Type</td><td>Organization-validated code signing, RSA 4096-bit, private key on a hardware token</td></tr>
                                            <tr>
                                                <td>Thumbprint (SHA-1)</td>
                                                <td><MonoBox value={THUMBPRINT} label="thumbprint" /></td>
                                            </tr>
                                            <tr><td>Serial number</td><td><code className="its-inline-mono">5DD4955A3F70604755798C88</code></td></tr>
                                            <tr><td>Valid</td><td>8 January 2026 to 9 February 2027</td></tr>
                                            <tr><td>Signed</td><td><code className="its-inline-mono">KhorDeveloperSetup.exe</code> (Authenticode, SHA-256) and the ClickOnce manifests. Signatures are timestamped (GlobalSign, RFC 3161), so they stay valid after the certificate expires.</td></tr>
                                            <tr>
                                                <td>Download</td>
                                                <td><a href="/KhorCodeSigning.cer" download className="its-download-link">KhorCodeSigning.cer</a> (public certificate only)</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <p>
                                    Certificates are renewed annually; the current one is valid until 9 February
                                    2027. Each renewal has a new thumbprint and serial number, but the same
                                    publisher (KHOR DEVELOPER LLC) and the same issuing CA (GlobalSign GCC R45
                                    CodeSigning CA 2020). Publisher-based rules in AppLocker or WDAC carry over.
                                    If you allow us by Defender certificate indicator, thumbprint or Trusted
                                    Publishers, add the new certificate when we notify you (2–4 weeks ahead) and
                                    keep the old one until it expires. Signatures from version 3.1.0.7 onward are
                                    timestamped, so installed versions keep working after the current certificate
                                    expires.
                                </p>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="endpoints">
                                <h2>Network Endpoints</h2>
                                <p>All HTTPS on port 443 unless noted.</p>
                                <div className="its-table-wrap">
                                    <table className="its-table">
                                        <thead>
                                            <tr><th>Endpoint</th><th>Used for</th><th>When</th></tr>
                                        </thead>
                                        <tbody>
                                            {ENDPOINTS.map((row) => (
                                                <tr key={row.endpoint}>
                                                    <td><code className="its-inline-mono">{row.endpoint}</code></td>
                                                    <td>{row.usedFor}</td>
                                                    <td>{row.when}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p>
                                    Sign-in pages also use Microsoft's or Google's standard sign-in domains, which most
                                    organizations already allow.
                                </p>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="permissions">
                                <h2>Permissions and Technical Details</h2>

                                <h3>About the Add-In</h3>
                                <ul>
                                    <li>A Microsoft Excel VSTO add-in (.NET Framework) for desktop Excel on Windows. It doesn't run in Excel for the web or on Mac.</li>
                                    <li>Publisher: Khor Developer LLC.</li>
                                    <li>Runs inside Excel as the signed-in Windows user. It installs no services, drivers or background processes, and contains no analytics or telemetry.</li>
                                </ul>

                                <div className="its-table-wrap">
                                    <table className="its-table">
                                        <thead>
                                            <tr><th>Requirement</th><th>Detail</th></tr>
                                        </thead>
                                        <tbody>
                                            <tr><td>Operating system</td><td>Windows</td></tr>
                                            <tr><td>Desktop Excel</td><td>Microsoft 365 desktop Excel on Windows</td></tr>
                                            <tr><td>.NET</td><td>.NET Framework 4.8</td></tr>
                                            <tr><td>Office runtime</td><td>Visual Studio 2010 Tools for Office Runtime (VSTO 4.0)</td></tr>
                                            <tr><td>Admin rights</td><td>Not needed for the add-in, which installs per user. They may be needed if .NET 4.8 or the VSTO runtime is missing; the installer downloads both from Microsoft.</td></tr>
                                        </tbody>
                                    </table>
                                </div>

                                <h3>Permissions</h3>
                                <ul>
                                    <li>Runs as the signed-in Windows user inside Excel. No admin rights are needed for the add-in or its updates.</li>
                                    <li>Like any desktop Excel add-in (and unlike browser-based web add-ins), Khor runs with the user's normal Windows permissions. It doesn't request elevation, runs only while Excel is open, writes only to the user's own profile, and connects only to the endpoints listed above.</li>
                                    <li>To convert Word and PowerPoint files for Khor AI, the add-in briefly opens Word or PowerPoint in the background on the PC. Temporary files are deleted afterwards.</li>
                                </ul>
                                <details className="its-details">
                                    <summary>Technical details: registry and profile paths</summary>
                                    <p>
                                        Writes only to that user's profile: HKCU registry keys written by Office/VSTO,{" "}
                                        <code className="its-inline-mono">%LOCALAPPDATA%</code>,{" "}
                                        <code className="its-inline-mono">%APPDATA%</code>,{" "}
                                        <code className="its-inline-mono">%TEMP%</code>.
                                    </p>
                                </details>
                            </section>
                        </AnimatedBlock>

                        <AnimatedBlock>
                            <section className="its-block" id="contact">
                                <h2>Contact</h2>
                                <p>
                                    <strong>Email</strong><br />
                                    <a href="mailto:support@khordeveloper.com">
                                        support@khordeveloper.com
                                    </a>
                                </p>
                            </section>
                        </AnimatedBlock>
                    </>
                )}
            </div>
        </section>
    );
};

function ItSecurity() {
    return (
        <div className="App">
            <Helmet>
                <title>IT & Security | Khor Developer</title>
                <meta name="robots" content="index, follow" />
                <meta
                    name="description"
                    content="Technical and security information for IT teams approving Khor Developer: Microsoft sign-in consent, code-signing certificate, network endpoints and data handling."
                />
                <meta property="og:title" content="IT & Security | Khor Developer" />
                <meta property="og:url" content="https://www.khordeveloper.com/it-security" />
                <link rel="canonical" href="https://www.khordeveloper.com/it-security" />
            </Helmet>
            <ReactHeader />
            <ItSecuritySection />
            <FooterSection />
        </div>
    );
}

export default ItSecurity;
